"use client";
import { Button } from "@/components/ui/button";
import { IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import { getTextColorForBackground } from "@/lib/utils";
import YoutubeVideoOptions from "./tabs/youtubeVideo/options";
import AdminPopover from "../../content/AdminPopover";
import { SettingTabs } from "../../content/SettingTabs";
import BlockToggle from "./tabs/toggle";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    theme: ITheme;
    newBlock?: string | null;
}
function extractVideoId(url: string): string | null {
    const regex =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);

    return match ? match[1] : null;
}
export default function BlockYoutubeVideo({
    admin,
    user,
    block,
    update,
    theme,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const text_color = theme.text_col_heading
        ? theme.text_col_heading
        : getTextColorForBackground(theme.background);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "YouTube Video",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <YoutubeVideoOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            title={`YouTube Video - ${blockUpdate.url}`}
            onClick={handleExpand}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <div className="flex w-full flex-col" style={{ color: text_color }}>
                {!blockUpdate.options?.hide_title && (
                    <h4>{blockUpdate.name}</h4>
                )}
                <iframe
                    id="youtube-video"
                    className="aspect-video h-full w-full rounded-lg"
                    src={`https://www.youtube.com/embed/${extractVideoId(blockUpdate.url)}?enablejsapi=1&autoplay=false&mute=true`}
                    allow=""
                    allowFullScreen
                />
            </div>
        </AdminPopover>
    );
}
